import { yupResolver } from '@hookform/resolvers/yup';
import { Camera, Navigation, Upload, X } from 'lucide-react';
import React, { memo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { PhotoUploadFormData, photoUploadSchema } from '../../schemas/photoSchemas';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  resetUploadForm,
  setDetectingLocation,
  setLocationPermission,
  setUploadProgress,
  updateLocationGPS,
  uploadPhotos,
} from '../../store/slices/photosSlice';
import type { PhotoCategory } from '../../types';
import { Dropdown, Input, TextArea } from '../forms';

interface PhotoUploadDrawerHookFormProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
  isProjectSpecific: boolean;
}

export const PhotoUploadDrawerHookForm: React.FC<PhotoUploadDrawerHookFormProps> = memo(
  ({ isOpen, onClose, onUploadSuccess, isProjectSpecific }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const toast = useToast();

    // Redux state
    const {
      categories,
      projects,
      uploading,
      uploadProgress,
      detectingLocation,
      locationPermission,
      uploadData,
    } = useAppSelector(state => state.photos);

    // React Hook Form setup
    const {
      control,
      handleSubmit,
      watch,
      setValue,
      reset,
      formState: { errors, isValid },
    } = useForm<PhotoUploadFormData>({
      resolver: yupResolver(photoUploadSchema) as any,
      defaultValues: {
        project_id: uploadData.project_id,
        files: [],
        title: '',
        category: 'progress' as PhotoCategory,
        number_on_image: '',
        location: {
          site: '',
          building: '',
          floor: '',
          room: '',
          gps: '',
        },
        description: '',
        tags: [],
      } as PhotoUploadFormData,
      mode: 'onChange',
    });

    // Watch form values for real-time updates
    const watchedFiles = watch('files');
    const watchedProjectId = watch('project_id');

    // Update form when Redux state changes
    useEffect(() => {
      if (uploadData.project_id && uploadData.project_id !== watchedProjectId) {
        setValue('project_id', uploadData.project_id);
      }
    }, [uploadData.project_id, setValue, watchedProjectId]);

    // Reset form when modal opens/closes
    useEffect(() => {
      if (isOpen) {
        reset({
          project_id: uploadData.project_id,
          files: [],
          title: '',
          category: 'progress' as PhotoCategory,
          number_on_image: '',
          location: {
            site: '',
            building: '',
            floor: '',
            room: '',
            gps: '',
          },
          description: '',
          tags: [],
        } as PhotoUploadFormData);
      }
    }, [isOpen, reset, uploadData.project_id]);

    // File upload handler
    const handleFileUpload = useCallback(
      async (files: FileList) => {
        const fileArray = Array.from(files);

        // Basic validation
        const errors: string[] = [];

        if (fileArray.length === 0) {
          errors.push('No files selected');
        }

        if (fileArray.length > 10) {
          errors.push('Maximum 10 files allowed per upload');
        }

        fileArray.forEach((file, index) => {
          if (!file.type.startsWith('image/')) {
            errors.push(`File ${index + 1} (${file.name}) is not an image`);
          }

          if (file.size > 10 * 1024 * 1024) {
            errors.push(`File ${index + 1} (${file.name}) exceeds 10MB limit`);
          }
        });

        if (errors.length > 0) {
          errors.forEach(error => {
            toast.error(error);
          });
          return;
        }

        setValue('files', fileArray, { shouldValidate: true });
      },
      [setValue, toast]
    );

    // Location detection
    const handleDetectLocation = useCallback(async () => {
      if (!navigator.geolocation) {
        alert(t('photos.upload.geolocationNotSupported'));
        return;
      }

      dispatch(setDetectingLocation(true));

      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000,
          });
        });

        const gpsString = `${position.coords.latitude}, ${position.coords.longitude}`;

        setValue('location.gps', gpsString);
        dispatch(updateLocationGPS(gpsString));
        dispatch(setLocationPermission('granted'));
      } catch (error: any) {
        console.error('Geolocation error:', error);

        if (error.code === error.PERMISSION_DENIED) {
          dispatch(setLocationPermission('denied'));
          alert(t('photos.upload.locationPermissionDenied'));
        } else if (error.code === error.TIMEOUT) {
          alert(t('photos.upload.locationTimeout'));
        } else {
          alert(t('photos.upload.locationError'));
        }
      } finally {
        dispatch(setDetectingLocation(false));
      }
    }, [dispatch, setValue, t]);

    // Form submission
    const onSubmit = async (data: PhotoUploadFormData) => {
      try {
        const uploadDataFormatted = {
          project_id: data.project_id,
          files: data.files.filter((file): file is File => file !== undefined),
          title: data.title,
          description: data.description || '',
          category: data.category,
          tags: data.tags || [],
          location: {
            site: data.location?.site || '',
            building: data.location?.building || '',
            floor: data.location?.floor || '',
            room: data.location?.room || '',
            gps: data.location?.gps || '',
          },
          number_on_image: data.number_on_image || '',
        };

        const result = await dispatch(
          uploadPhotos({
            uploadData: uploadDataFormatted,
            onProgress: progress => dispatch(setUploadProgress(progress)),
            projectId: isProjectSpecific ? data.project_id : undefined,
          })
        ).unwrap();

        if (result.success) {
          onClose();
          onUploadSuccess?.();
          reset();
          dispatch(resetUploadForm());
        }
      } catch (error) {
        console.error('Upload failed:', error);
      }
    };

    // Format file size
    const formatFileSize = (bytes: number) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (!isOpen) return null;

    const drawerContent = (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[110]" onClick={onClose} />

        {/* Drawer */}
        <div className="fixed top-6 right-6 bottom-6 w-full max-w-2xl bg-white shadow-xl rounded-xl border border-gray-200 z-[120] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <Camera className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('photos.upload.title')}</h3>
                <p className="text-sm text-gray-600">
                  {isProjectSpecific
                    ? t('photos.upload.projectDescription')
                    : t('photos.upload.globalDescription')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={uploading}
              className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <form
              id="photo-form"
              onSubmit={handleSubmit(onSubmit as any)}
              className="p-6 space-y-6"
            >
              {/* Project Selection */}
              {!isProjectSpecific && (
                <Controller
                  name="project_id"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      label={t('photos.upload.project')}
                      placeholder={t('photos.upload.selectProject')}
                      options={projects}
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={errors.project_id?.message}
                      disabled={uploading}
                    />
                  )}
                />
              )}

              {/* File Upload */}
              <div className="space-y-4">
                <label className="block text-sm font-semibold text-gray-900">
                  {t('photos.upload.photos')} <span className="text-red-500">*</span>
                </label>

                <Controller
                  name="files"
                  control={control}
                  render={() => (
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-red-300 hover:bg-red-50/30 transition-all duration-200 group">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={e => {
                          if (e.target.files) {
                            handleFileUpload(e.target.files);
                          }
                        }}
                        className="hidden"
                        id="photo-upload"
                        disabled={uploading}
                      />
                      <label
                        htmlFor="photo-upload"
                        className="cursor-pointer flex flex-col items-center space-y-3"
                      >
                        <div className="bg-red-50 p-4 rounded-full group-hover:bg-red-100 transition-colors">
                          <Camera className="h-8 w-8 text-red-500" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-base font-medium text-gray-900">
                            {t('photos.upload.dragDropOrClick')}
                          </p>
                          <p className="text-sm text-gray-500">
                            {t('photos.upload.supportedFormats')}
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                />

                {errors.files && (
                  <p className="text-sm text-red-600 flex items-center space-x-1 mt-2">
                    <span>⚠️</span>
                    <span>{errors.files.message}</span>
                  </p>
                )}

                {/* Selected Files */}
                {watchedFiles && watchedFiles.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {t('photos.upload.selectedFiles')}
                      </h4>
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                        {watchedFiles.length} files
                      </span>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {watchedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded-lg border border-gray-100"
                        >
                          <span className="truncate font-medium text-gray-900">{file?.name}</span>
                          <span className="text-gray-500 ml-3 font-mono text-xs">
                            {file && formatFileSize(file.size)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Photo Details */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">
                  Photo Details
                </h3>

                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('photos.upload.title')}
                      placeholder={t('photos.upload.titlePlaceholder')}
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={errors.title?.message}
                      disabled={uploading}
                      required
                    />
                  )}
                />

                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      label={t('photos.upload.category')}
                      placeholder={t('photos.upload.selectCategory')}
                      options={categories.map(c => ({ value: c.value, label: c.label }))}
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={errors.category?.message}
                      disabled={uploading}
                      required
                    />
                  )}
                />

                <Controller
                  name="number_on_image"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('photos.upload.taggingId')}
                      placeholder={t('photos.upload.taggingIdPlaceholder')}
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={errors.number_on_image?.message}
                      disabled={uploading}
                    />
                  )}
                />
              </div>

              {/* Location Information */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">
                  {t('photos.upload.location')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="location.site"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label={t('photos.upload.site')}
                        placeholder={t('photos.upload.sitePlaceholder')}
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={errors.location?.site?.message}
                        disabled={uploading}
                        className="h-11"
                      />
                    )}
                  />

                  <Controller
                    name="location.building"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label={t('photos.upload.building')}
                        placeholder={t('photos.upload.buildingPlaceholder')}
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={errors.location?.building?.message}
                        disabled={uploading}
                        className="h-11"
                      />
                    )}
                  />

                  <Controller
                    name="location.floor"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label={t('photos.upload.floor')}
                        placeholder={t('photos.upload.floorPlaceholder')}
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={errors.location?.floor?.message}
                        disabled={uploading}
                        className="h-11"
                      />
                    )}
                  />

                  <Controller
                    name="location.room"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label={t('photos.upload.room')}
                        placeholder={t('photos.upload.roomPlaceholder')}
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={errors.location?.room?.message}
                        disabled={uploading}
                        className="h-11"
                      />
                    )}
                  />
                </div>

                {/* GPS Location */}
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <label className="block text-sm font-semibold text-gray-900">
                    {t('photos.upload.gpsLocation')}{' '}
                    <span className="text-gray-500 font-normal">(Optional)</span>
                  </label>

                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={handleDetectLocation}
                      disabled={detectingLocation || uploading}
                      className="flex items-center space-x-2 px-4 py-2.5 text-sm border border-red-200 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Navigation className="h-4 w-4" />
                      <span>
                        {detectingLocation
                          ? t('photos.upload.detectingLocation')
                          : t('photos.upload.detectLocation')}
                      </span>
                    </button>

                    {locationPermission === 'denied' && (
                      <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                        {t('photos.upload.locationPermissionDenied')}
                      </span>
                    )}
                  </div>

                  {watch('location.gps') && (
                    <div className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                      <strong>{t('photos.upload.coordinates')}:</strong> {watch('location.gps')}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">
                  Additional Information
                </h3>

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label={t('photos.upload.description')}
                      placeholder={t('photos.upload.descriptionPlaceholder')}
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={errors.description?.message}
                      disabled={uploading}
                      rows={4}
                      className="resize-none"
                    />
                  )}
                />
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm font-medium">
                      <span className="text-gray-700">{t('photos.upload.uploading')}</span>
                      <span className="text-red-600 font-semibold">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-red-500 to-red-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-6 bg-white rounded-b-xl">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={uploading}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                form="photo-form"
                disabled={uploading || !isValid}
                className="px-6 py-2 text-sm font-medium text-white bg-red-500 border border-transparent rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    {t('photos.upload.uploading')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {t('photos.upload.uploadPhotos')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </>
    );

    return createPortal(drawerContent, document.body);
  }
);

PhotoUploadDrawerHookForm.displayName = 'PhotoUploadDrawerHookForm';
